/**
 * STAYTOQ 브랜드 마크 — **Q 컷** (2026-10-10). 도형의 단일 진실원.
 *
 * 이름 끝의 Q 는 '톡'을 흔한 TALK 대신 TOQ 로 적은 데서 온, 이름을 가장 이름답게 만드는 글자다. 그 Q 하나를 마크로 쓴다 — 두꺼운 고리와 45° 꼬리,
 * 꼬리가 고리를 지나는 자리를 파낸 틈. 한 가지 색으로 성립하고(그라디언트·그림자 없음),
 * 워드마크 **옆에** 선다. 예전 마크(고리와 점)는 이름의 O 자리에 앉았지만 이 마크는 Q 라
 * O 자리에 넣으면 STAYTQQ 로 읽힌다.
 *
 * 왜 이 모양인가 — 시안 비교판(2026-10-10)에서 셋 중에 골랐다:
 *  - 점을 뺐다. '점 = 손님 질문 하나'는 설명을 들어야 아는 뜻이었다.
 *  - **꼬리는 고리 안쪽(구멍)에서 출발한다.** 고리 바깥에서 시작한 첫 렌더는 돋보기로 읽혔다.
 *  - 틈은 꼬리 양옆 6 씩. 16px 에서는 틈이 뭉개지고 Q 실루엣만 남는다 — 그게 의도다.
 *
 * 도형은 viewBox 100 안의 **채운 경로 둘**이다(마스크·stroke 없음). 그래서 Satori(공유 카드)·
 * 위젯 DOM·PNG 생성기·PDF 가 같은 문자열을 그대로 쓴다. 숫자는 이렇게 나왔다 —
 * 중심 (46,46) · 고리 바깥 r42 / 안쪽 r20 · 틈 = 대각선 기준 ±14 를 걷어냄 ·
 * 꼬리 = 같은 대각선 위 너비 16, (52,52)→(90,90). 잉크 상자가 4~95.7 이라 광학 중심이 맞는다.
 *
 * 이 값을 베껴 쓰는 곳(독립 문서라 import 를 못 한다): `src/app/icon.svg` · `scripts/generate-deck.mjs`.
 * 둘은 `tests/unit/brand-mark.test.ts` 가 이 파일과 문자열로 대조한다.
 */

export const MARK_VIEWBOX = '0 0 100 100';

/**
 * 마크 색 — **강조 글자 초록과 같은 값**(사용자 결정 2026-10-10, 팔레트 A '비비드 에메랄드').
 * 밝은 바탕은 marketing.css `--ember-forest`(흰 4.89 · 캔버스 4.69 — 브랜드 초록 #00B868 의 글자용 값), 어두운 바탕은 `--ember-lift`(미드나이트 9.95).
 * 2026-10-11 계열 전체를 색상 154°·채도 100% 로 옮겼다(와이어트 초록의 장점) — 밝은 값은 밝기 그대로, 어두운 바탕 값만 파스텔 민트 #3BE6A0 → #0BD880.
 * 글자와 마크가 같은 초록이어야 한다는 요청이라, 글자 기준(4.5)을 넘는 값으로 묶고 표면 따라 짝을 바꾼다.
 * 같은 날 잠깐 #0BA060 한 값(양쪽 3.0 통과)을 썼다 — 로고로는 넘겨도 글자 기준엔 못 미쳐 글자와 같은 색이 될 수 없었다.
 * CSS 에서는 이 리터럴이 아니라 `--mark-ink` 토큰을 쓴다(글자 초록이 갈리는 자리에서 같이 갈린다).
 */
export const MARK_COLOR = '#00824A';

/** 어두운 바탕(미드나이트·검정 타일·검정 런처) 위 마크 — 어두운 표면의 글자 초록과 같은 값. */
export const MARK_COLOR_ON_DARK = '#0BD880';

/** 고리 — 오른쪽 아래가 대각선 따라 끊긴 두꺼운 원. */
export const MARK_RING_D = 'M83.90 64.10 A42 42 0 1 0 64.10 83.90 L46.20 66.00 A20 20 0 1 1 66.00 46.20 Z';

/** 꼬리 — 구멍 안에서 출발해 틈을 지나 밖으로 나가는 45° 막대. */
export const MARK_TAIL_D = 'M57.66 46.35 L95.66 84.35 L84.35 95.66 L46.35 57.66 Z';

export const MARK_PATHS = [MARK_RING_D, MARK_TAIL_D] as const;

/**
 * 독립 SVG 문자열 — PNG 생성기·PDF·공유 카드(data URI)용. 색은 부르는 쪽이 정한다.
 * `size` 를 주면 width/height 를 박고, 안 주면 바깥 상자를 따른다.
 */
export function markSvg(color: string, size?: number): string {
  const wh = size ? ` width="${size}" height="${size}"` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${MARK_VIEWBOX}"${wh}>`
    + MARK_PATHS.map(d => `<path d="${d}" fill="${color}"/>`).join('')
    + '</svg>';
}

/** `<img src>` 로 쓰는 data URI — Satori 는 인라인 SVG 요소보다 이미지를 안정적으로 그린다. */
export function markDataUri(color: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(markSvg(color))}`;
}
